var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/*
 * Copyright (c) 2024 LS ELECTRIC Co. Ltd. All Reserved.
 */
/*******************************************************************
 * @author HyeongjinOH
 * @contact hjoh@detech.co.kr
 * @date 2024-02-20
 * @brief SEI 랜딩 로그인 - 고도화
 *
 ********************************************************************/
import { useEffect, useState } from "react";
import { useAsync } from "react-async";
//recoil
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { alertState, noneUrl } from "../../../recoil/assessmentState";
import { getApiLangState, langId, langState } from "../../../recoil/langState";
import { contactusState, urlState } from "../../../recoil/menuState";
import { authId, userInfoState } from "../../../recoil/userState";
// utils
import * as CONST from "../../../utils/Const";
import * as HTTPUTIL from "../../../utils/api/HttpUtil";
import * as CUTIL from "../../../utils/commUtils";
import * as i18n from "../../../utils/langs/i18nUtils";
import * as USERUTILS from "../../../utils/user/userUtils";
import { Trans } from "react-i18next";
import { useTrans } from "../../../utils/langs/useTrans";
import Captcha from "../Captcha";
function LandingLoginPop() {
    // trans, navigate, ref
    const navigate = useNavigate();
    const t = useTrans();
    // use recoil
    const [langs, setRecoilLangs] = useRecoilState(langState); // recoil langState
    const apiLang = useRecoilValue(getApiLangState);
    const langIds = useRecoilValue(langId);
    const [userInfo, setRecoilUserInfo] = useRecoilState(userInfoState); // recoil userState
    const setRecoliUserId = useSetRecoilState(authId);
    const [alert, setAlert] = useRecoilState(alertState);
    const [recilUrl, setRecoilUrl] = useRecoilState(urlState);
    const setRecoilNoneUrlInfo = useSetRecoilState(noneUrl);
    const [reContactusState, setReContactusState] = useRecoilState(contactusState);
    const [userId, setUserId] = useState(""); //test@test.com
    const [userPw, setUserPw] = useState(""); //1234qwer!@
    const [certification, setCertification] = useState("");
    // 초기 로그인 유지 여부를 "Y"로 셋팅
    localStorage.setItem(CONST.STR_KEEP_LOGIN_YN, "Y");
    const [keepLoginYn, setKeepLoginYn] = useState(localStorage.getItem(CONST.STR_KEEP_LOGIN_YN) == null ? "N" : localStorage.getItem(CONST.STR_KEEP_LOGIN_YN));
    // useAsync call axios
    const [errorList, setErrorList] = useState([]);
    // 이상징후 판별(CAPTCHA) - 비밀번호 연속 불일치 4회 이상부터 서버가 captchaRequired 를 내려준다.
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [captchaKey, setCaptchaKey] = useState("");
    const [captchaValue, setCaptchaValue] = useState("");
    const [captchaReload, setCaptchaReload] = useState(0); // 값이 바뀌면 CAPTCHA 재발급
    const [passwordOpen, setPasswordOpen] = useState(false); // 비밀번호 표시/숨김 토글
    const { data: data, isLoading, run, } = useAsync({
        deferFn: HTTPUTIL.Http,
        httpMethod: "POST",
        appPath: "/auth/login",
        appQuery: {
            userId: userId,
            password: userPw,
            language: apiLang,
            captchaKey: captchaRequired ? captchaKey : null,
            captchaValue: captchaRequired ? captchaValue : null,
        },
    });
    useEffect(() => {
        let errL = "";
        if (data) {
            // 이상징후 판별 - 서버가 CAPTCHA 노출 여부를 내려준다.
            // 발급키는 1회용이라 로그인이 실패할 때마다 새 CAPTCHA 를 받아와야 한다.
            if (data.data && data.data.captchaRequired === true) {
                setCaptchaRequired(true);
                setCaptchaReload((prev) => prev + 1);
            }
            else if ((data.data && data.data.captchaRequired === false) || data.codeNum == 200) {
                // 서버가 CAPTCHA 불필요를 명시(계정 잠김 등)하거나 로그인 성공 시 CAPTCHA 를 숨긴다.
                setCaptchaRequired(false);
                setCaptchaKey("");
                setCaptchaValue("");
            }
            if (data.codeNum == 200) {
                // sessionStorage
                sessionStorage.setItem(CONST.STR_TOKEN, data.body.token);
                sessionStorage.setItem(CONST.STR_TOKEN_EXPIRETIME, data.body.tokenExpireTime);
                // localStorage
                localStorage.setItem(CONST.STR_USERID, userId);
                localStorage.setItem(CONST.STR_USERROLE, data.body.role);
                localStorage.setItem(CONST.STR_REFRESHTOKEN, data.body.refreshToken);
                localStorage.setItem(CONST.STR_REFRESHTOKEN_EXPIRETIME, data.body.refreshTokenExprieTime);
                localStorage.removeItem("demo");
                (() => __awaiter(this, void 0, void 0, function* () {
                    // IFFE(Immediately Invoked Function Expression)은 함수가 정의되자마자 실행되는 자바스크립트 기술입니다.
                    const detailUserInfo = yield USERUTILS.getDetailUserInfoR(data.body);
                    const lang = CUTIL.frontLangSet(detailUserInfo.language);
                    yield i18n.changeLang(lang);
                    //
                    setRecoilLangs(lang);
                    //
                    setRecoilUserInfo({
                        loginTime: detailUserInfo.loginTime,
                        userId: detailUserInfo.userId,
                        email: detailUserInfo.email,
                        userName: detailUserInfo.userName,
                        phoneNumber: detailUserInfo.phoneNumber,
                        companyName: detailUserInfo.companyName,
                        zoneName: detailUserInfo.zoneName,
                        department: detailUserInfo.department,
                        role: detailUserInfo.role,
                        language: detailUserInfo.language,
                        classificationCode: detailUserInfo.classificationCode,
                        classification: detailUserInfo.classification,
                        agreeMailReceipt: detailUserInfo.agreeMailReceipt,
                        agreeTos: detailUserInfo.agreeTos,
                        agreePersonalInfo: detailUserInfo.agreePersonalInfo,
                        agreeData: detailUserInfo.agreeData,
                        userIdPk: detailUserInfo.userIdPk,
                        isAutomatedNickName: detailUserInfo.isAutomatedNickName,
                        nickName: detailUserInfo.nickName,
                        userRank: detailUserInfo.userRank,
                        userImage: detailUserInfo.userImage,
                        loginInfo: data.body,
                    });
                    // setRecoilUrl(startlUrlInfo.url);
                    let reUrl = localStorage.getItem("loginintro");
                    setRecoilUrl(recilUrl);
                    navigate(recilUrl);
                    window.location.reload();
                }))();
            }
            else if (data.codeNum == 113) {
                Close("pop-login");
                setAlert(Object.assign(Object.assign({}, alert), { header: langIds > 1 ? "Guide" : "안내", divTag: data.data.errorList[0].msg, callBack: () => {
                        setReContactusState(Object.assign(Object.assign({}, reContactusState), { code: "C01_07", codeName: "COMMUNITY", description: langIds > 1 ? "Community Usage Inquiry" : "커뮤니티 이용 문의", id: langIds > 1 ? 922 : 921 }));
                        setRecoilUrl(CONST.URL_CONTACTUS);
                        navigate(CONST.URL_CONTACTUS);
                        setRecoilNoneUrlInfo(true);
                    } }));
                CUTIL.jsopen_AlertPopup(CONST.POP_ALERT);
            }
            else if (data.codeNum == 999) {
                setErrorList(data.data.errorList);
                setUserPw(""); // 로그인 실패 시 비밀번호 입력란 초기화
            }
            else {
                setErrorList(data.data.errorList);
                setUserPw(""); // 로그인 실패 시 비밀번호 입력란 초기화
                if (data.errorList.some((error) => error.msg.includes("인증") || data.errorList.some((error) => error.msg.includes("authenticated")))) {
                    Close("pop-login");
                    setRecoliUserId(userId);
                    CUTIL.jsopen_AlertPopup("cmm-join-ok");
                    // if (!CUTIL.isnull(logCertification)) logCertification.click();
                }
            }
        }
    }, [data]);
    useEffect(() => {
        setCertification(userId);
    }, [userId]);
    // ID
    function handleSetUserId(e) {
        setErrorList([]);
        setUserId(e.target.value);
    }
    // 비밀번호
    function handleSetUserPw(e) {
        setErrorList([]);
        setUserPw(e.target.value);
    }
    // 로그인 버튼
    function onClickLogin(e) {
        run();
    }
    // 엔터 버튼
    function handleonKeyDown(e) {
        if (e.key === "Enter") {
            // Enter 입력이 되면 클릭 이벤트 실행
            onClickLogin(e);
        }
    }
    // 닫기 버튼
    function Close(id) {
        setUserId("");
        setUserPw("");
        setErrorList([]);
        setCaptchaValue("");
        CUTIL.jsclose_Popup(id);
    }
    function onClickPop(e) {
        Close("pop-login");
        CUTIL.jsopen_Popup(e);
    }
    return (_jsx(_Fragment, { children: _jsxs("div", Object.assign({ id: "pop-login", className: "popup-layer js-layer hidden popup-basic w350 pop-login" }, { children: [_jsxs("div", Object.assign({ className: "popup__head" }, { children: [_jsx("h1", { children: t("라벨.로그인") }), _jsx("button", Object.assign({ className: "btn btn-close js-close", onClick: (e) => Close("pop-login") }, { children: _jsx("span", Object.assign({ className: "hide" }, { children: "\uB2EB\uAE30" })) }))] })), _jsx("div", Object.assign({ className: "popup__body" }, { children: _jsxs("div", Object.assign({ className: "login-wrap" }, { children: [_jsxs(Trans, Object.assign({ i18nKey: "로그인정보" }, { children: [_jsx("p", { children: _jsx("strong", { children: "\uB85C\uADF8\uC778\uC774 \uD544\uC694\uD55C \uC11C\uBE44\uC2A4\uC785\uB2C8\uB2E4." }) }), _jsx("p", { children: "\uD68C\uC6D0\uAC00\uC785 \uB610\uB294 \uB85C\uADF8\uC778\uD558\uACE0 \uD568\uAED8 \uC18C\uD1B5\uD574\uC8FC\uC138\uC694!" })] })), _jsx("div", Object.assign({ className: "login__form" }, { children: _jsx("form", Object.assign({ action: "#", method: "", name: "loginform" }, { children: _jsxs("fieldset", { children: [_jsx("legend", { children: "\uB85C\uADF8\uC778 \uC815\uBCF4 \uC785\uB825" }), _jsxs("ul", Object.assign({ className: "form__input" }, { children: [_jsxs("li", { children: [_jsx("label", Object.assign({ htmlFor: "userId", className: "hide" }, { children: "\uC544\uC774\uB514 \uC785\uB825" })), _jsx("div", Object.assign({ className: "input__area" }, { children: _jsx("input", { type: "text", id: "userId", placeholder: t("라벨.아이디"), style: { width: "100%", boxSizing: "border-box" }, className: errorList.length > 0 ? "input-error" : "", value: userId, onChange: (e) => handleSetUserId(e) }) }))] }), _jsxs("li", Object.assign({ style: { marginBottom: errorList.length > 0 ? "2.5rem" : undefined } }, { children: [_jsx("label", Object.assign({ htmlFor: "userPw", className: "hide" }, { children: "\uBE44\uBC00\uBC88\uD638 \uC785\uB825" })), _jsxs("div", Object.assign({ className: "input__area" }, { children: [_jsx("input", { type: passwordOpen ? "text" : "password", id: "userPw", placeholder: t("라벨.비밀번호"), style: { width: "100%", boxSizing: "border-box", paddingRight: "2.75rem" }, className: errorList.length > 0 ? "input-error" : "", value: userPw, onChange: (e) => handleSetUserPw(e), onKeyDown: (e) => handleonKeyDown(e) }), errorList.length === 0 && (_jsx("button", Object.assign({ type: "button", className: "btn-eye", style: { top: "50%", transform: "translateY(-50%)" }, onClick: () => setPasswordOpen(!passwordOpen) }, { children: _jsx("i", { className: passwordOpen ? "icon-eye" : "icon-eye-off" }) }))), _jsx("p", Object.assign({ className: "input-errortxt" }, { children: errorList.filter((err) => err.field === "password").map((err) => err.msg).length > 0
                                                                            ? errorList.filter((err) => err.field === "password").map((err) => err.msg)
                                                                            : errorList.filter((err) => err.field === "cntError").length > 0
                                                                                ? errorList.filter((err) => err.field === "cntError").map((err) => err.msg)
                                                                                : errorList.filter((err) => err.field === "userId").map((err) => err.msg) }))] }))] })), captchaRequired && (_jsx(Captcha, { value: captchaValue, onChange: setCaptchaValue, onCaptchaKeyChange: setCaptchaKey, reloadTrigger: captchaReload, onKeyDown: (e) => handleonKeyDown(e), isError: errorList.filter((err) => err.field === "cntError").length > 0, placeholder: t("라벨.보안문자") }))] })), _jsx("ul", Object.assign({ className: "form__etc" }, { children: _jsxs("li", { children: [_jsx("button", Object.assign({ type: "button", className: "btn-txt js-open", "data-pop": "cmm-findId", onClick: (e) => onClickPop(e) }, { children: _jsx("span", { children: t("라벨.아이디찾기") }) })), _jsx("button", Object.assign({ type: "button", className: "btn-txt js-open", "data-pop": "cmm-findPw", onClick: (e) => onClickPop(e) }, { children: _jsx("span", { children: t("라벨.비밀번호찾기") }) }))] }) })), _jsx("button", Object.assign({ type: "button", className: "btn-login", onClick: run }, { children: _jsx("span", { children: t("라벨.로그인") }) }))] }) })) })), _jsx("ul", Object.assign({ className: "join" }, { children: _jsxs("li", { children: [_jsx("p", { children: t("라벨.아직회원이아닌가요") }), _jsx("button", Object.assign({ type: "button", className: "btn btn-txt js-open", "data-pop": "cmm-join", onClick: (e) => onClickPop(e) }, { children: _jsx("span", { children: t("라벨.회원가입") }) }))] }) }))] })) }))] })) }));
}
export default LandingLoginPop;
